import React, { KeyboardEvent, PointerEvent, useRef, useState } from 'react';
type PredictionSliderProps = {
  value: number;
  touched: boolean;
  onChange: (value: number) => void;
};
const MIN = 0;
const MAX = 100;
const ticks = Array.from({
  length: 21
}, (_, i) => i * 5);
const labelled = [0, 25, 50, 75, 100];
export function PredictionSlider({
  value,
  touched,
  onChange
}: PredictionSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const commit = (next: number) => {
    const clamped = Math.max(MIN, Math.min(MAX, Math.round(next)));
    if (clamped !== value && clamped % 10 === 0 && 'vibrate' in navigator) {
      navigator.vibrate?.(4);
    }
    onChange(clamped);
  };
  const fromClientX = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return value;
    const ratio = (clientX - rect.left) / rect.width;
    return MIN + Math.max(0, Math.min(1, ratio)) * (MAX - MIN);
  };
  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    commit(fromClientX(e.clientX));
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 10 : 1;
    const map: Record<string, number> = {
      ArrowRight: value + step,
      ArrowUp: value + step,
      ArrowLeft: value - step,
      ArrowDown: value - step,
      PageUp: value + 10,
      PageDown: value - 10,
      Home: MIN,
      End: MAX
    };
    if (e.key in map) {
      e.preventDefault();
      commit(map[e.key]);
    }
  };
  const pct = (value - MIN) / (MAX - MIN) * 100;
  return <div className="select-none" data-id="element-56" data-mp="1xcmixxeea9yj">
      <div ref={trackRef} className="relative h-14 cursor-pointer touch-none" onPointerDown={handlePointerDown} onPointerMove={e => dragging && commit(fromClientX(e.clientX))} onPointerUp={() => setDragging(false)} onPointerCancel={() => setDragging(false)} data-id="element-57" data-mp="1xbupas4cymaw">
        <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-hairline" data-id="element-58" data-mp="2zonpasgi4j42" />
        {touched && <div className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-accent-fill" style={{
        width: `${pct}%`
      }} data-id="element-59" data-mp="2unzya6s9w29u" />}
        {ticks.map(t => <div key={t} className={`absolute top-1/2 w-px ${labelled.includes(t) ? 'h-3 bg-faint' : 'h-1.5 bg-hairline'}`} style={{
        left: `${t}%`,
        transform: 'translate(-50%, 8px)'
      }} data-id="element-60" data-mp="2kr3o7wmq7836" />)}
        <div role="slider" tabIndex={0} aria-label="Estimated difference in FVC decline at Week 52" aria-valuemin={MIN} aria-valuemax={MAX} aria-valuenow={value} aria-valuetext={touched ? `${value} millilitres` : 'Not set'} onKeyDown={handleKeyDown} className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-4 focus-visible:ring-offset-paper" style={{
        left: `${pct}%`
      }} data-id="element-61" data-mp="2bxqks0r1co2u">
          <div className={`h-8 w-8 rounded-full border-2 shadow-[0_2px_10px_rgba(0,0,0,0.25)] transition-transform duration-150 ease-out ${touched ? 'border-accent-fill bg-accent-fill' : 'border-ink bg-surface'} ${dragging ? 'scale-110' : 'scale-100 hover:scale-105'}`} data-id="element-62" data-mp="cm4k19gxd9gn" />
        </div>
      </div>
      <div className="relative mt-1 h-5 text-xs tabular-nums text-faint" data-id="element-63" data-mp="3q07u5uhc1dh7">
        {labelled.map(t => <span key={t} className="absolute -translate-x-1/2 whitespace-nowrap" style={{
        left: `${t}%`
      }} data-id="element-64" data-mp="3uxhhn4l5vf2e">
            {t === 0 || t === 100 ? `${t} mL` : t}
          </span>)}
      </div>
    </div>;
}